import type { TFunction } from 'i18next'

import type {
    EmploymentMetaTypeDTO,
    EmploymentType,
    ExecutionAmountMetaDTO,
    SessionInfoDTO,
} from '@leanpay/models'
import type { Option } from '@leanpay/utils'
import {
    EmploymentContractType,
    ExecutionAmountDTO,
    Gender,
    LatePaymentTime,
    TwoFAStatus,
} from '@leanpay/models'
import { getCountryPhoneCodes, getIbanValidationOptions, Market } from '@leanpay/utils'

import {
    currentMarket,
    isMarketRom,
    isMarketSlo,
    isProdEnvironment,
    isStageEnvironment,
    isTestEnvironment,
    PreQualifiedRoute,
} from './constants'

export const getYesNoSelectOption = (translate: any): Option[] => [
    {
        label: translate('financialDetails.labels.yes'),
        value: true,
    },
    {
        label: translate('financialDetails.labels.no'),
        value: false,
    },
]

export const getExecutionAmountOptionsSLO = (
    translate: (key: string, params?: object) => string,
    currency?: string,
): Option[] => [
    {
        label: translate('enum.executionAmount.LOWER_THAN_100', {
            executionAmount100Start: '0',
            currency: currency ?? '€',
            executionAmount100End: '100',
        }),
        value: ExecutionAmountDTO.LOWER_THAN_100,
    },
    {
        label: translate('enum.executionAmount.BETWEEN_101_200', {
            executionAmount101_200Start: '101',
            executionAmount101_200End: '200',
            currency: currency ?? '€',
        }),
        value: ExecutionAmountDTO.BETWEEN_101_200,
    },
    {
        label: translate('enum.executionAmount.GREATER_THAN_200', {
            executionAmount200Start: '200',
            currency: currency ?? '€',
        }),
        value: ExecutionAmountDTO.GREATER_THAN_200,
    },
]

export const getFinancialDetailsOptions = (
    translate: any,
    financialDetailsOptionsData?: string[],
) => {
    return (
        financialDetailsOptionsData?.map((option) => ({
            label: translate(`enum.numberOfConsecutivePaymentMonths.${option}`),
            value: option,
        })) ?? []
    )
}

export const getNrLoansInLastYear = (
    translate: TFunction,
    financialDetailsOptionsData?: string[],
) => {
    return (
        financialDetailsOptionsData?.map((option) => ({
            //@ts-ignore
            label: String(translate(`enum.nrLoansInLastYear.${option}`)),
            value: option,
        })) ?? []
    )
}
export const getNrLoansDelay = (translate: TFunction, financialDetailsOptionsData?: string[]) => {
    return (
        financialDetailsOptionsData?.map((option) => ({
            //@ts-ignore
            label: String(translate(`enum.nrLoansDelaysInLastYear.${option}`)),
            value: option,
        })) ?? []
    )
}

export const getEmploymentTypesOptions = (
    translate: any,
    employmentTypeData?: EmploymentMetaTypeDTO[],
): Option[] => {
    if (!employmentTypeData) return []
    return employmentTypeData.map((option) => ({
        label: translate(`enum.employmentType.${option.name}`),
        value: option.name,
    }))
}
export const getEmploymentContractType = (
    translate: any,
    selectedEmploymentType: EmploymentType | null,
    employmentTypeData?: EmploymentMetaTypeDTO[],
) => {
    if (!employmentTypeData || !selectedEmploymentType) return []

    //Selected employmentType type
    const filteredEmploymentType = employmentTypeData
        .filter(
            (type) =>
                !type.contractTypes.includes(EmploymentContractType.DO_NOT_WANT_TO_ASK) &&
                type.contractTypes.length > 1,
        )
        .find((employmentType) => employmentType.name === selectedEmploymentType)

    return (
        filteredEmploymentType?.contractTypes.map((type) => ({
            label: translate(`enum.employmentContractType.${type}`),
            value: type,
        })) ?? []
    )
}

const numberOfDependants: number[] = [0, 1, 2, 3, 4, 5, 6]
const numberOfChildren: number[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]
export const countryPhoneCodes = getCountryPhoneCodes(currentMarket, isProdEnvironment)

export const numberOfDependantsOptions = numberOfDependants.map((option) => ({
    label: String(option),
    value: option,
}))
export const numberOfChildrenOptions = numberOfChildren.map((option) => ({
    label: String(option),
    value: option,
}))

export const getGenderOptions = (translate: any) => {
    const options = [
        {
            value: Gender.Male,
            label: translate('enum.gender.MALE'),
        },
        {
            value: Gender.Female,
            label: translate('enum.gender.FEMALE'),
        },
    ]
    return options
}

export const isExistingMembership = (sessionInfo: SessionInfoDTO | null) => {
    if (!sessionInfo) return false
    return (
        sessionInfo.membershipStatus !== 'NOT_SUBMITTED' &&
        sessionInfo.membershipStatus !== 'RESTARTED'
    )
}

export const getLatePayingLoanOptions = (translate: any) => {
    return Object.keys(LatePaymentTime).map((paymentTime) => {
        return {
            value: paymentTime,
            label: translate(`enum.latePaymentTime.${paymentTime}`),
        }
    })
}

export const handlePreQualifiedProgress = (
    route: string,
    setProgress: (progress: number) => void,
) => {
    if (isMarketSlo) return handlePrequalifyProgressSLO(route, setProgress)
    if (isMarketRom) return handlePrequalifyProgressROM(route, setProgress)
}

const handlePrequalifyProgressSLO = (route: string, setProgress: (progress: number) => void) => {
    switch (route.replace('/', '')) {
        case PreQualifiedRoute.PrequalifiedIntro:
            setProgress(10)
            break
        case PreQualifiedRoute.RequestOtp:
            setProgress(20)
            break
        case PreQualifiedRoute.UpdateUserInfo:
            setProgress(30)
            break
        case PreQualifiedRoute.Login:
            setProgress(40)
            break
        case PreQualifiedRoute.PersonalDetails:
            setProgress(50)
            break
        case PreQualifiedRoute.TwoFaPin:
            setProgress(60)
            break
        case PreQualifiedRoute.FinancialDetails:
            setProgress(70)
            break
        case PreQualifiedRoute.PreviewFinancialDetails:
            setProgress(80)
            break
        case PreQualifiedRoute.ValidateOffer:
            setProgress(90)
            break
        case PreQualifiedRoute.PrequalifiedSuccess:
            setProgress(100)
            break
        case PreQualifiedRoute.PrequalifiedError:
            setProgress(100)
            break

        default:
            setProgress(100)
            break
    }
}
const handlePrequalifyProgressROM = (route: string, setProgress: (progress: number) => void) => {
    switch (route.replace('/', '')) {
        case PreQualifiedRoute.PrequalifiedIntro:
            setProgress(10)
            break
        case PreQualifiedRoute.RequestOtp:
            setProgress(20)
            break
        case PreQualifiedRoute.UpdateUserInfo:
            setProgress(30)
            break
        case PreQualifiedRoute.Login:
            setProgress(40)
            break
        case PreQualifiedRoute.PersonalDetails:
            setProgress(50)
            break
        case PreQualifiedRoute.FinancialDetails:
            setProgress(60)
            break
        case PreQualifiedRoute.AddressDetails:
            setProgress(70)
            break
        case PreQualifiedRoute.TwoFaPin:
            setProgress(80)
            break
        case PreQualifiedRoute.ValidateOffer:
            setProgress(90)
            break
        case PreQualifiedRoute.PrequalifiedSuccess:
            setProgress(100)
            break
        case PreQualifiedRoute.PrequalifiedError:
            setProgress(100)
            break
        default:
            setProgress(100)
            break
    }
}

export const shouldSetTwoFaPin = (sessionInfo?: SessionInfoDTO | null) => {
    if (!sessionInfo) return false
    return sessionInfo.twoFAStatus === TwoFAStatus.NOT_SET && sessionInfo
}

export const ibanValidationOptions = getIbanValidationOptions(Market.SLO)

export const shouldShowProgressBar = () => {
    const progressBarExcludedPaths = [
        '/',
        '/privacy-policy',
        '/terms-of-use',
        '/vendor-mock',
        '/check-limit',
        '/login',
        '/request-otp',
        '/complaint-policy',
        '/representative-example',
        '/financial-data-agreement',
        '/credit-bureau-agreement',
        '/cookie-policy',
        '/digital-verification-policy',
    ]

    return !progressBarExcludedPaths.includes(window.location.pathname)
}

export const shouldShowCancelIcon = () => {
    const cancelIconExcludedPaths = [
        '/',
        '/privacy-policy',
        '/terms-of-use',
        '/vendor-mock',
        '/complaint-policy',
        '/representative-example',
        '/financial-data-agreement',
        '/credit-bureau-agreement',
        '/cookie-policy',
        '/digital-verification-policy',
        '/prequalified-success',
        '/prequalified-error',
        '/check-limit',
        '/login',
        '/request-otp',
    ]

    return !cancelIconExcludedPaths.includes(window.location.pathname)
}

export const getExecutionAmountOptions = (executionAmounts?: ExecutionAmountMetaDTO[]) =>
    executionAmounts?.map((e) => ({
        value: e.id,
        label: e.name,
    })) ?? []

export const isStandalonePublicRoute = [
    '/kyc-verification',
    '/payment-status',
    '/privacy-policy',
    '/complaint-policy',
    '/terms-of-use',
    '/representative-example',
    '/hardware-and-software-compatibility',
    '/financial-data-agreement',
    '/representative-example',
    '/cookie-policy',
    '/digital-verification-policy',
].includes(window.location.pathname)

export const getAppLink = () => {
    switch (currentMarket) {
        case Market.SLO:
            if (isTestEnvironment) {
                return 'https://test-app.leanpay.si'
            }
            if (isStageEnvironment) {
                return 'https://stage-app.leanpay.si'
            }
            if (isProdEnvironment) {
                return 'https://app.leanpay.si'
            }
            return 'localhost:4200'

        default:
            return ''
    }
}
